// Email validation utilities
export const validateEmail = (email) => {
  const errors = [];
  
  // Check if email is provided
  if (!email || email.trim() === '') {
    errors.push('Email address is required');
    return { isValid: false, errors };
  }

  const trimmedEmail = email.trim();

  // Basic format validation using regex
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  if (!emailRegex.test(trimmedEmail)) {
    errors.push('Please enter a valid email address');
  }

  // Length validation
  if (trimmedEmail.length > 254) {
    errors.push('Email address is too long (maximum 254 characters)');
  }

  // Local part validation (before @)
  const [localPart, domain] = trimmedEmail.split('@');
  
  if (localPart && localPart.length > 64) {
    errors.push('Email local part is too long (maximum 64 characters)');
  }

  // Domain validation
  if (domain) {
    // Check for consecutive dots
    if (domain.includes('..')) {
      errors.push('Email domain cannot contain consecutive dots');
    }
    
    // Check domain length
    if (domain.length > 253) {
      errors.push('Email domain is too long (maximum 253 characters)');
    }
    
    // Check for valid domain format
    const domainRegex = /^[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!domainRegex.test(domain)) {
      errors.push('Email domain format is invalid');
    }
  }

  // Check for common typos in popular domains
  const commonDomainTypos = {
    'gmail.co': 'gmail.com',
    'gmail.cm': 'gmail.com',
    'gmai.com': 'gmail.com',
    'yahoo.co': 'yahoo.com',
    'yahoo.cm': 'yahoo.com',
    'hotmail.co': 'hotmail.com',
    'hotmail.cm': 'hotmail.com',
    'outlook.co': 'outlook.com',
    'outlook.cm': 'outlook.com'
  };

  if (domain && commonDomainTypos[domain.toLowerCase()]) {
    errors.push(`Did you mean ${localPart}@${commonDomainTypos[domain.toLowerCase()]}?`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    suggestion: domain && commonDomainTypos[domain.toLowerCase()] ? `${localPart}@${commonDomainTypos[domain.toLowerCase()]}` : null
  };
};

// Real-time email validation for input fields
export const validateEmailRealTime = (email) => {
  if (!email || email.trim() === '') {
    return { isValid: true, errors: [] }; // Don't show errors for empty field during typing
  }

  const validation = validateEmail(email);
  return validation;
};

// Email validation for forms (stricter)
export const validateEmailForSubmission = (email, fieldName = 'Email') => {
  const validation = validateEmail(email);
  
  if (!validation.isValid) {
    return {
      isValid: false,
      errors: validation.errors.map(error => `${fieldName}: ${error}`)
    };
  }

  return { isValid: true, errors: [] };
};

// Bulk email validation for CSV uploads
export const validateEmailsInBulk = (emails) => {
  const results = [];
  const duplicates = [];
  const emailSet = new Set();

  emails.forEach((email, index) => {
    const validation = validateEmail(email);
    
    // Check for duplicates
    const normalizedEmail = email.toLowerCase().trim();
    if (emailSet.has(normalizedEmail)) {
      duplicates.push({ index, email, error: 'Duplicate email address' });
    } else {
      emailSet.add(normalizedEmail);
    }

    results.push({
      index,
      email,
      isValid: validation.isValid,
      errors: validation.errors,
      suggestion: validation.suggestion
    });
  });

  return {
    results,
    duplicates,
    totalValid: results.filter(r => r.isValid).length,
    totalInvalid: results.filter(r => !r.isValid).length
  };
};

// Email domain validation for organizational emails
export const validateOrganizationalEmail = (email, allowedDomains = []) => {
  const validation = validateEmail(email);
  
  if (!validation.isValid) {
    return validation;
  }

  if (allowedDomains.length > 0) {
    const domain = email.split('@')[1]?.toLowerCase();
    if (!allowedDomains.includes(domain)) {
      return {
        isValid: false,
        errors: [`Email must be from one of these domains: ${allowedDomains.join(', ')}`]
      };
    }
  }

  return validation;
};

// Email formatting utility
export const formatEmail = (email) => {
  if (!email) return '';
  return email.trim().toLowerCase();
};

// Check if email looks like a temporary/disposable email
export const isDisposableEmail = (email) => {
  const disposableDomains = [
    '10minutemail.com',
    'tempmail.org',
    'guerrillamail.com',
    'mailinator.com',
    'throwaway.email',
    'temp-mail.org',
    'yopmail.com'
  ];
  
  const domain = email.split('@')[1]?.toLowerCase();
  return disposableDomains.includes(domain);
};